import { useState, useCallback, useMemo } from "react";
import { useSettingsRHF } from "../hooks/useSettingsRHF";
import { expensesSchema } from "../types/settingsSchemas";
import type { ExpensesSettingsData, PlannedExpense } from "../types/settings";

/**
 * Field classification for Expenses form (two-tier visibility)
 *
 * Essential: shown during onboarding + Settings
 * Advanced: shown only in Settings (behind ShowMoreToggle)
 */
export const EXPENSES_ESSENTIAL_FIELDS = [
  "housing",
  "utilities",
  "food",
  "transportation",
  "insurance",
  "healthcare",
  "other",
  "mortgagePayment",
  "creditCardPayment",
  "carLoanPayment",
  "lineOfCreditPayment",
  "studentLoanPayment",
  "otherLiabilitiesPayment",
] as const;

export const EXPENSES_ADVANCED_FIELDS = ["personalDiscretionary"] as const;

/**
 * Shared logic for Expenses functionality
 * Handles form state, validation, and data management
 */
export function useExpensesLogic() {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const {
    total: totalExpenses,
    handleChange,
    handleBlur,
    ...rest
  } = useSettingsRHF<ExpensesSettingsData>("expenses", expensesSchema, [
    "housing",
    "utilities",
    "food",
    "transportation",
    "insurance",
    "healthcare",
    "other",
    "mortgagePayment",
    "creditCardPayment",
    "carLoanPayment",
    "lineOfCreditPayment",
    "studentLoanPayment",
    "otherLiabilitiesPayment",
    "personalDiscretionary",
  ]);

  const toggleAdvanced = () => setShowAdvanced((prev) => !prev);

  // ── Planned Expenses Handlers ──

  const plannedExpenses: PlannedExpense[] = useMemo(
    () => rest.formData.plannedExpenses ?? [],
    [rest.formData.plannedExpenses]
  );

  const handleAddPlannedExpense = useCallback(() => {
    const updated = [
      ...plannedExpenses,
      { description: "", amount: 0, year: new Date().getFullYear() + 1 },
    ];
    handleChange("plannedExpenses", updated);
    handleBlur("plannedExpenses", updated);
  }, [plannedExpenses, handleChange, handleBlur]);

  const handleRemovePlannedExpense = useCallback(
    (index: number) => {
      const updated = plannedExpenses.filter((_, i) => i !== index);
      handleChange("plannedExpenses", updated);
      handleBlur("plannedExpenses", updated);
    },
    [plannedExpenses, handleChange, handleBlur]
  );

  const handlePlannedExpenseChange = useCallback(
    (index: number, field: string, value: unknown) => {
      const updated = plannedExpenses.map((expense, i) =>
        i === index ? { ...expense, [field]: value } : expense
      );
      handleChange("plannedExpenses", updated);
      handleBlur("plannedExpenses", updated);
    },
    [plannedExpenses, handleChange, handleBlur]
  );

  return {
    ...rest,
    handleChange,
    handleBlur,
    totalExpenses,
    showAdvanced,
    toggleAdvanced,
    plannedExpenses,
    handleAddPlannedExpense,
    handleRemovePlannedExpense,
    handlePlannedExpenseChange,
  };
}
